<template>
  <div :id="widgetIdInternal" class="cloudflare-turnstile-widget"></div>
</template>

<script>
export default {
  name: 'CloudflareTurnstileManual',
  props: {
    theme: {
      type: String,
      default: 'light', // Options: 'light', 'dark', 'auto'
    },
    size: {
      type: String,
      default: 'normal', // Options: 'normal', 'compact'
    },
    appearance: {
      type: String,
      default: 'interaction-only', // Options: 'always', 'execute', 'interaction-only'
    },
    // Optional prop to set the Cloudflare Turnstile site key:
    // sitekeyProp: {
    //   type: String,
    //   default: null
    // }
  },
  data() {
    return {
      value: null, // This will hold the Turnstile token
      siteKey: '0x4AAAAAABc9gp6NrgE7-2qj',
      widgetId: null, // Stores the ID returned by turnstile.render() for cleanup/reset
      widgetIdInternal: 'cf-turnstile-' + Math.random().toString(36).substring(2, 15) // Unique ID for the div
    };
  },
  mounted() {
    this.initializeTurnstile();
  },
  beforeDestroy() {
    // Clean up the widget when the component is destroyed
    if (this.widgetId && typeof window.turnstile !== 'undefined') {
      window.turnstile.remove(this.widgetId);
      this.widgetId = null;
    }
  },
  methods: {
    initializeTurnstile() {
      // Ensure the Turnstile script is loaded and window.turnstile is available
      if (typeof window.turnstile !== 'undefined') {
        this.renderWidget();
      } else {
        // Poll for the Turnstile API if it's not immediately available
        let attempts = 0;
        const intervalId = setInterval(() => {
          attempts++;
          if (typeof window.turnstile !== 'undefined') {
            clearInterval(intervalId);
            this.renderWidget();
          } else if (attempts > 20) { // Stop after 20 attempts (e.g., 10 seconds)
            clearInterval(intervalId);
            console.error('Cloudflare Turnstile API (window.turnstile) not loaded after several attempts.');
            this.$emit('error', 'api-not-loaded');
          }
        }, 500);
      }
    },
    renderWidget() {
      // const effectiveSiteKey = this.sitekeyProp || this.siteKey;
      const effectiveSiteKey = this.siteKey; // Using data property for now

      if (!effectiveSiteKey || effectiveSiteKey === 'YOUR_CLOUDFLARE_SITE_KEY') {
        console.error('Cloudflare Turnstile: siteKey is not configured. Please update it in cloudflare.vue.');
        this.$emit('error', 'sitekey-not-configured');
        return;
      }

      // Ensure the element exists in the DOM before trying to render into it
      const targetElement = document.getElementById(this.widgetIdInternal);
      if (targetElement) {
        try {
          this.widgetId = window.turnstile.render(`#${this.widgetIdInternal}`, {
            sitekey: effectiveSiteKey,
            callback: this.handleSuccess,       // Called when a token is generated
            'expired-callback': this.handleExpired, // Called when a token expires
            'error-callback': this.handleError,     // Called when an error occurs
            theme: this.theme,
            size: this.size,
            appearance: this.appearance,
            // 'response-field-name': 'cf-turnstile-response', // Default, can be customized if needed
            // 'response-field': true, // Default, creates a hidden input
          });
        } catch (error) {
          console.error('Error rendering Cloudflare Turnstile widget:', error);
          this.$emit('error', 'render-error');
        }
      } else {
        // This might happen if the component mounts but the template isn't in the DOM yet, or ID is wrong.
        // Vue's $nextTick might be useful or ensuring render is called after DOM is fully ready.
        console.error(`Cloudflare Turnstile: Target element #${this.widgetIdInternal} for rendering not found. Retrying in next tick.`);
        this.$nextTick(() => {
            const retryTargetElement = document.getElementById(this.widgetIdInternal);
            if (retryTargetElement && !this.widgetId) { // Check !this.widgetId to prevent re-rendering if already done
                 this.widgetId = window.turnstile.render(`#${this.widgetIdInternal}`, {
                    sitekey: effectiveSiteKey,
                    callback: this.handleSuccess,
                    'expired-callback': this.handleExpired,
                    'error-callback': this.handleError,
                    theme: this.theme,
                    size: this.size,
                    appearance: this.appearance,
                });
            } else if (!retryTargetElement) {
                console.error(`Cloudflare Turnstile: Target element #${this.widgetIdInternal} still not found on retry.`);
                this.$emit('error', 'element-not-found-retry');
            }
        });
      }
    },
    handleSuccess(token) {
      this.value = token;
      this.$emit('verify', token); // For compatibility with how parent might listen
    },
    handleExpired() {
      this.value = null;
      this.$emit('expired');
      // Turnstile typically handles auto-retry. If explicit reset needed from parent, it calls resetRecaptcha.
    },
    handleError(errorCode) {
      console.error('Cloudflare Turnstile error code:', errorCode);
      this.value = null;
      this.$emit('error', errorCode);
    },
    resetRecaptcha() {
      this.value = null;
      if (this.widgetId && typeof window.turnstile !== 'undefined') {
        window.turnstile.reset(this.widgetId);
      } else {
        // If widgetId is not set, it implies renderWidget might not have succeeded or run.
        // Attempting to re-initialize might be an option, or log a warning.
        console.warn('Cloudflare Turnstile: resetRecaptcha called, but widgetId is not available. Widget might not have rendered.');
        // Optionally, you could try to re-render if it seems appropriate for your UX:
        // this.initializeTurnstile(); 
      }
    }
  }
};
</script>

<style scoped>
.cloudflare-turnstile-widget {
  /* Adjust min-height and min-width based on the 'size' prop if needed */
  /* 'normal' size is 300x65px. 'compact' is 130x120px. */
  min-height: 65px; 
  min-width: 300px; 
  display: inline-block; /* or 'block' or other, depending on layout needs */
  /* You can add a border or background for debugging visibility if needed */
  /* border: 1px solid #ccc; */
}
</style>


// WEBPACK FOOTER //
// lib/brave-authentication/components/cloudflare.vue